// @givmo/app-charity — the catalog-removal request page: "Remove from Givmo" at `/removal` (CPA-WEB7;
// re-addressed and retitled at Org Machinery OM-17's cut-over, RULED 2026-09-07 — the address is
// `SETTINGS_ROUTES.removal`, the composed settings page's removal card is a DOOR into it).
//
// The home of the request-to-be-removed flow (the console's settings live on the composed settings
// page behind the gear and the sidebar's LAST row; admin management is the Admin Console's Team
// access page, `/org/team-access`). It orchestrates:
//   - CHARITY + ROLE → the ACTIVE-org context (CPA-WEB3): a multi-org admin picks the org in the shell
//     switcher; single-org admins default to their one org; the active org's role drives the Owner gate.
//   - REMOVAL REQUESTS → `charityAdminListCharityRemovalRequests` (GET …/removal-requests) → `{ requests }`
//     (newest first). An OPEN request (REQUESTED / IDENTITY_VERIFIED) → the pending panel; otherwise the
//     request flow (Owner only).
//
// OWNER GATE — FAIL CLOSED (task §5): the request affordance (<RemovalRequestForm>) renders ONLY once the
// caller is POSITIVELY confirmed an Owner of the ACTIVE org (a non-null active membership + the Owner role) —
// requesting removal is `charity:remove_request` = Owner. A pending/errored membership read, or a Member
// role → NO request control (an honest "ask a Global Admin" note instead: the console calls the org-level
// role Global Admin, never "Owner" — R6; CPA-WEB94). The backend also 403s a non-Owner
// POST. The GET is Owner-or-Member, so a Member DOES see an already-open request read-only (the pending
// panel) — they just can't start one.
//
// The root keeps `data-testid="page-settings"` (the data contract e2e/guard assert) — the ADDRESS is what
// tells this page from the composed settings page, whose root wears the same id.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { charityAdminListCharityRemovalRequestsOptions } from '@givmo/api-client/query';
import { isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { Building2, Lock, ShieldAlert } from 'lucide-react';
import { isOwnerRole } from '../admins/admins-roles';
import { useActiveOrg } from '../../org/active-org-context';
import { extractRemovalRequests, openRemovalRequest } from './removal-decode';
import { RemovalPendingView } from './removal-pending-view';
import { RemovalRequestForm } from './removal-request-form';
import { EXTERNAL_STATUS_REFETCH_MS } from '../../lib/external-status-refresh';

const PREFIX = 'settings';

export function SettingsPage() {
  const queryClient = useQueryClient();

  // ── Resolve the ACTIVE org + role (active-org context; multi-org admins switch in the shell). ─────
  const {
    activeMembership: membership,
    activeCharityId: charityId,
    isPending,
    isError,
    error,
    refetch,
  } = useActiveOrg();
  // Owner gate — FAIL CLOSED: the request control renders ONLY once the caller is positively an Owner of
  // the ACTIVE org. (A non-null active membership implies the read succeeded.)
  const canRequest = membership !== null && isOwnerRole(membership.role);

  // ── Load the removal-request history (only once we know the charity). ────────────────────────────
  const removalOptions = charityAdminListCharityRemovalRequestsOptions({
    path: { charity_id: charityId ?? '' },
  });
  const removalQuery = useQuery({
    ...removalOptions,
    enabled: charityId != null,
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      openRemovalRequest(extractRemovalRequests(query.state.data))
        ? EXTERNAL_STATUS_REFETCH_MS
        : false,
  });
  const requests = extractRemovalRequests(removalQuery.data);
  const openRequest = openRemovalRequest(requests);

  const invalidateRemovals = () =>
    queryClient.invalidateQueries({ queryKey: removalOptions.queryKey });

  // ── Loading / error / no-org gates ──────────────────────────────────────────────────────────────
  if (isPending) {
    return (
      <PageShell>
        <Card className="p-24" testId={`${PREFIX}-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (isError) {
    return (
      <PageShell>
        <EmptyState
          testId={`${PREFIX}-memberships-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your organization"
          description={
            isGivmoError(error)
              ? error.message
              : 'We couldn’t load your organization. Please try again.'
          }
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={refetch}
              testId={`${PREFIX}-memberships-retry`}
            >
              Try again
            </Button>
          }
        />
      </PageShell>
    );
  }

  if (!membership || !charityId) {
    // No verified org yet → nothing to remove; direct them to claim first.
    return (
      <PageShell>
        <EmptyState
          testId={`${PREFIX}-no-org`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Claim your organization first"
          description="Once your organization is verified you can manage its settings here."
        />
      </PageShell>
    );
  }

  if (removalQuery.isPending) {
    return (
      <PageShell orgName={membership.name}>
        <Card className="p-24" testId={`${PREFIX}-removal-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (removalQuery.isError) {
    return (
      <PageShell orgName={membership.name}>
        <EmptyState
          testId={`${PREFIX}-removal-error`}
          icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
          title="Couldn’t load your removal status"
          description={
            isGivmoError(removalQuery.error)
              ? removalQuery.error.message
              : 'We couldn’t load your removal status. Please try again.'
          }
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void removalQuery.refetch()}
              testId={`${PREFIX}-removal-retry`}
            >
              Try again
            </Button>
          }
        />
      </PageShell>
    );
  }

  // ── An OPEN request already exists → the pending panel (shown to Owner AND Member; the GET is
  //    Owner-or-Member). The idempotent backend 409s a second request, so we never offer the button. ──
  if (openRequest) {
    return (
      <PageShell orgName={membership.name}>
        <RemovalPendingView
          request={openRequest}
          lastCheckedAt={removalQuery.dataUpdatedAt}
          refreshing={removalQuery.isFetching}
          onRefresh={() => void removalQuery.refetch()}
        />
      </PageShell>
    );
  }

  // ── No open request. Owner → the request flow. Member → fail-closed "ask a Global Admin" note. ────
  if (!canRequest) {
    return (
      <PageShell orgName={membership.name}>
        <div
          className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-14"
          {...testIdProp(`${PREFIX}-owner-only-note`)}
        >
          <Icon icon={Lock} size={18} className="mt-1 shrink-0 text-text-tertiary" aria-hidden />
          <p className="text-s13 text-text-secondary">
            Only a Global Admin can request to remove this organization from Givmo. Ask a Global
            Admin if you need to make this change.
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell orgName={membership.name}>
      <RemovalRequestForm
        charityId={charityId}
        orgName={membership.name}
        onRequested={invalidateRemovals}
      />
    </PageShell>
  );
}

// ── Page frame ──────────────────────────────────────────────────────────────────────────────────────
function PageShell({ children, orgName }: { children: React.ReactNode; orgName?: string }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[720px] flex-col gap-16 px-24 py-24"
      {...testIdProp('page-settings')}
    >
      <PageHeader
        title="Remove from Givmo"
        subtitle="Manage your organization on Givmo."
        organizationName={orgName}
        organizationNameTestId={`${PREFIX}-org-badge`}
      />
      {children}
    </div>
  );
}
